import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { api } from '../App';

const VerifyEmailPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const {
    data,
    isLoading,
    isError,
  } = useQuery(
    ['verify-email', token],
    async () => {
      const response = await api.get(`/auth/verify-email/${token}`);
      return response.data;
    },
    {
      enabled: !!token,
      retry: false,
    },
  );

  const isSuccess = data?.success === true;

  return (
    <div className="min-h-screen bg-white dark:bg-background-dark-secondary flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="card max-w-md w-full">
          <div className="p-6 space-y-4 text-center">
            {isLoading ? (
              <>
                <div className="flex justify-center mb-2">
                  <div className="h-8 w-8 border-2 border-primary-700 border-t-transparent rounded-full animate-spin" />
                </div>
                <p className="text-text-secondary dark:text-text-dark-secondary">Verifying your email address…</p>
              </>
            ) : isError || !isSuccess ? (
              <>
                <div className="flex justify-center mb-2">
                  <AlertCircle className="h-10 w-10 text-amber-500" />
                </div>
                <h1 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary">
                  Verification link is invalid or expired
                </h1>
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-2">
                  The email verification link is no longer valid. You can request a new verification email from your
                  profile page after signing in.
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="mt-4 inline-flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium bg-primary-700 text-white hover:bg-primary-800"
                >
                  Go to login
                </button>
              </>
            ) : (
              <>
                <div className="flex justify-center mb-2">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
                <h1 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary">
                  Email verified successfully
                </h1>
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-2">
                  Your email address has been verified. You can now continue using Capitol Commons.
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="mt-4 inline-flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium bg-primary-700 text-white hover:bg-primary-800"
                >
                  Go to dashboard
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;

